import type { Dayjs } from 'dayjs';
import {
  resolveCompanyBalanceRange,
  type CompanyBalancePeriod,
} from '../companyBalance/companyBalancePeriod';
import { localeForLanguage } from '../../../utils/numberFormatting';
import { MARGIN_BUCKET_THRESHOLDS, type MarginBucket } from '../profitabilityAnalysis/analysis';
import type {
  IncomeStatementBasis,
  IncomeStatementCustomer,
  IncomeStatementCustomerOrder,
  IncomeStatementLine,
  IncomeStatementMonth,
} from './incomeStatementApi';

// Pure helpers behind the "Rachunek wyników" tab — no React, no fetching, so
// every branch here is exercised by fast unit tests instead of RTL renders.
// See docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md for the underlying
// business rules; this module only shapes already-normalized report data for
// the period picker, the P&L table, the two drill-down donuts and the
// customers table.

// ─── Periods ────────────────────────────────────────────────────────────────

export type IncomeStatementPeriodGroupKey = 'year' | 'halfYear' | 'quarter' | 'month';

export interface IncomeStatementPeriodOption {
  value: string;
  period: CompanyBalancePeriod;
  anchor: string; // YYYY-MM
  from: string;
  to: string;
  inProgress: boolean;
  isCurrentMonth: boolean;
}

export interface IncomeStatementPeriodGroup {
  key: IncomeStatementPeriodGroupKey;
  options: IncomeStatementPeriodOption[];
}

/**
 * Year: current + previous year. Half-year/quarter: current + 3 previous
 * (stepping back by their own span so boundaries stay aligned). Month:
 * current + 11 previous. Every option's own span is at most 12 months —
 * well inside the backend's 24-month cap.
 */
export function buildPeriodOptions(now: Dayjs): IncomeStatementPeriodGroup[] {
  const currentMonthValue = now.format('YYYY-MM');

  function buildOption(period: CompanyBalancePeriod, rawAnchor: Dayjs): IncomeStatementPeriodOption {
    const range = resolveCompanyBalanceRange(rawAnchor, period);
    // Canonicalize the anchor to the resolved period's own start month, so
    // any day/month within e.g. a year yields the same stable option value
    // as `defaultPeriodValue` (`YEAR:${now.format('YYYY-01')}`).
    const anchor = range.start.format('YYYY-MM');
    return {
      value: `${period}:${anchor}`,
      period,
      anchor,
      from: range.from,
      to: range.to,
      inProgress: range.to >= currentMonthValue,
      isCurrentMonth: period === 'MONTH' && anchor === currentMonthValue,
    };
  }

  const yearOptions = [0, 1].map((delta) => buildOption('YEAR', now.subtract(delta, 'year')));
  const halfYearOptions = [0, 1, 2, 3].map((delta) => buildOption('HALF_YEAR', now.subtract(delta * 6, 'month')));
  const quarterOptions = [0, 1, 2, 3].map((delta) => buildOption('QUARTER', now.subtract(delta * 3, 'month')));
  const monthOptions = Array.from({ length: 12 }, (_, delta) => buildOption('MONTH', now.subtract(delta, 'month')));

  return [
    { key: 'year', options: yearOptions },
    { key: 'halfYear', options: halfYearOptions },
    { key: 'quarter', options: quarterOptions },
    { key: 'month', options: monthOptions },
  ];
}

const PERIOD_VALUES: CompanyBalancePeriod[] = ['MONTH', 'QUARTER', 'HALF_YEAR', 'YEAR'];

export function parsePeriodValue(value: string): { period: CompanyBalancePeriod; anchor: string } | null {
  const separatorIndex = value.indexOf(':');
  if (separatorIndex < 0) return null;
  const period = value.slice(0, separatorIndex) as CompanyBalancePeriod;
  const anchor = value.slice(separatorIndex + 1);
  if (!PERIOD_VALUES.includes(period) || !/^\d{4}-\d{2}$/.test(anchor)) return null;
  return { period, anchor };
}

export function defaultPeriodValue(now: Dayjs): string {
  return `YEAR:${now.format('YYYY-01')}`;
}

function monthAbbreviation(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat(localeForLanguage(language), {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Dropdown label, e.g. "Rok 2026 (sty–wrz, w toku)" or "Wrzesień 2026 (bieżący)". */
export function periodOptionLabel(
  option: IncomeStatementPeriodOption,
  t: TranslateFn,
  language: string,
): string {
  const [yearText, monthText] = option.anchor.split('-');
  const year = Number(yearText);
  const monthNumber = Number(monthText);

  if (option.period === 'MONTH') {
    const label = new Intl.DateTimeFormat(localeForLanguage(language), {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
    const capped = label.charAt(0).toUpperCase() + label.slice(1);
    return option.isCurrentMonth
      ? `${capped} (${t('reports:profitability.incomeStatement.periodCurrentMonth')})`
      : capped;
  }

  const range = `${monthAbbreviation(option.from, language)}–${monthAbbreviation(option.to, language)}`;
  const suffix = option.inProgress ? `, ${t('reports:profitability.incomeStatement.periodInProgress')}` : '';

  if (option.period === 'YEAR') {
    return `${t('reports:profitability.incomeStatement.periodYear', { year })} (${range}${suffix})`;
  }
  if (option.period === 'HALF_YEAR') {
    const index = monthNumber < 7 ? 'I' : 'II';
    return `${t('reports:profitability.incomeStatement.periodHalfYear', { index, year })} (${range}${suffix})`;
  }
  const index = ['I', 'II', 'III', 'IV'][Math.floor((monthNumber - 1) / 3)];
  return `${t('reports:profitability.incomeStatement.periodQuarter', { index, year })} (${range}${suffix})`;
}

/** Month-column header, e.g. "wrz 26" — mirrors ProfitabilityAnalysisWorkspace.monthLabel. */
export function monthShortLabel(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat(localeForLanguage(language), {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

// ─── P&L rows ───────────────────────────────────────────────────────────────

export type PnlRowKind = 'line' | 'group' | 'child' | 'total' | 'final' | 'percent';

export interface PnlRow {
  key: string;
  kind: PnlRowKind;
  sign?: 'minus' | 'equals';
  label: { key: string } | { category: string };
  hintKey?: string;
  group?: 'fixed';
  values: (number | null)[];
  total: number | null;
  isPercent: boolean;
  signed: boolean;
}

/**
 * Row order: revenue, orderCosts(minus), margin(total), marginPercent,
 * operatingFixedCosts(group,minus) + one child per category with a non-zero
 * value in any month, ebitda(total), ebitdaPercent, depreciation(minus),
 * financialCosts(minus), incomeTax(minus), profit(final), profitPercent.
 */
export function buildPnlRows(
  months: IncomeStatementMonth[],
  totals: IncomeStatementLine,
  basis: IncomeStatementBasis,
  categoryOrder: readonly string[],
): PnlRow[] {
  const lines = months.map((month) => month[basis]);
  const values = (pick: (line: IncomeStatementLine) => number | null) => lines.map(pick);
  const totalOf = (pick: (line: IncomeStatementLine) => number | null) => pick(totals);

  const categoriesPresent = new Set<string>();
  lines.forEach((line) => {
    Object.entries(line.operatingFixedCosts.byCategory).forEach(([category, amount]) => {
      if (amount !== 0) categoriesPresent.add(category);
    });
  });
  const orderedCategories = [
    ...categoryOrder.filter((category) => categoriesPresent.has(category)),
    ...Array.from(categoriesPresent).filter((category) => !categoryOrder.includes(category)).sort(),
  ];

  const rows: PnlRow[] = [
    {
      key: 'revenue',
      kind: 'line',
      label: { key: 'revenue' },
      values: values((line) => line.revenue),
      total: totalOf((line) => line.revenue),
      isPercent: false,
      signed: false,
    },
    {
      key: 'orderCosts',
      kind: 'line',
      sign: 'minus',
      label: { key: 'orderCosts' },
      hintKey: 'orderCostsHint',
      values: values((line) => line.orderCosts),
      total: totalOf((line) => line.orderCosts),
      isPercent: false,
      signed: false,
    },
    {
      key: 'margin',
      kind: 'total',
      sign: 'equals',
      label: { key: 'margin' },
      values: values((line) => line.margin),
      total: totalOf((line) => line.margin),
      isPercent: false,
      signed: true,
    },
    {
      key: 'marginPercent',
      kind: 'percent',
      label: { key: 'marginPercent' },
      values: values((line) => line.marginPercent),
      total: totalOf((line) => line.marginPercent),
      isPercent: true,
      signed: true,
    },
    {
      key: 'operatingFixedCosts',
      kind: 'group',
      sign: 'minus',
      group: 'fixed',
      label: { key: 'operatingFixedCosts' },
      hintKey: 'operatingFixedCostsHint',
      values: values((line) => line.operatingFixedCosts.total),
      total: totalOf((line) => line.operatingFixedCosts.total),
      isPercent: false,
      signed: false,
    },
    ...orderedCategories.map((category): PnlRow => ({
      key: `fixed:${category}`,
      kind: 'child',
      group: 'fixed',
      label: { category },
      values: values((line) => line.operatingFixedCosts.byCategory[category] ?? 0),
      total: totalOf((line) => line.operatingFixedCosts.byCategory[category] ?? 0),
      isPercent: false,
      signed: false,
    })),
    {
      key: 'ebitda',
      kind: 'total',
      sign: 'equals',
      label: { key: 'ebitda' },
      values: values((line) => line.ebitda),
      total: totalOf((line) => line.ebitda),
      isPercent: false,
      signed: true,
    },
    {
      key: 'ebitdaPercent',
      kind: 'percent',
      label: { key: 'ebitdaPercent' },
      values: values((line) => line.ebitdaPercent),
      total: totalOf((line) => line.ebitdaPercent),
      isPercent: true,
      signed: true,
    },
    {
      key: 'depreciation',
      kind: 'line',
      sign: 'minus',
      label: { key: 'depreciation' },
      values: values((line) => line.depreciation),
      total: totalOf((line) => line.depreciation),
      isPercent: false,
      signed: false,
    },
    {
      key: 'financialCosts',
      kind: 'line',
      sign: 'minus',
      label: { key: 'financialCosts' },
      values: values((line) => line.financialCosts),
      total: totalOf((line) => line.financialCosts),
      isPercent: false,
      signed: false,
    },
    {
      key: 'incomeTax',
      kind: 'line',
      sign: 'minus',
      label: { key: 'incomeTax' },
      // Whole-period hint, not per-month: an entered INCOME_TAX cost anywhere
      // in the totals overrides the 9% estimate for that basis (see
      // sumIncomeStatementLines / docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md).
      hintKey: totals.incomeTaxSource === 'entered' ? 'incomeTaxHintEntered' : 'incomeTaxHintEstimated',
      values: values((line) => line.incomeTax),
      total: totalOf((line) => line.incomeTax),
      isPercent: false,
      signed: false,
    },
    {
      key: 'profit',
      kind: 'final',
      sign: 'equals',
      label: { key: basis === 'actual' ? 'profitActual' : 'profitForecast' },
      values: values((line) => line.profit),
      total: totalOf((line) => line.profit),
      isPercent: false,
      signed: true,
    },
    {
      key: 'profitPercent',
      kind: 'percent',
      label: { key: 'profitPercent' },
      values: values((line) => line.profitPercent),
      total: totalOf((line) => line.profitPercent),
      isPercent: true,
      signed: true,
    },
  ];
  return rows;
}

// ─── Donuts ─────────────────────────────────────────────────────────────────

export interface DonutNode {
  key: string;
  name: string;
  value: number | null;
  children?: DonutNode[];
  customerKey?: string;
  folded?: boolean;
}

export const DONUT_MAX_SLICES = 6;

/**
 * Caps a node list at `max` slices: the top (max-1) by value plus a
 * drillable "Pozostałe (n)" node folding the rest (recursively, so a very
 * long tail still bottoms out at `max` slices per level). Nodes with a
 * `null` value (unknown amount) are dropped — they cannot be drawn or
 * summed meaningfully; zero/negative values are kept.
 */
export function foldSlices(
  nodes: DonutNode[],
  restLabel: (count: number) => string,
  max: number = DONUT_MAX_SLICES,
): DonutNode[] {
  const withValue = nodes.filter((node) => node.value != null);
  const sorted = [...withValue].sort((a, b) => (b.value as number) - (a.value as number));
  if (sorted.length <= max) return sorted;
  const top = sorted.slice(0, max - 1);
  const rest = sorted.slice(max - 1);
  const restValue = rest.reduce((sum, node) => sum + (node.value as number), 0);
  return [
    ...top,
    {
      key: 'rest',
      name: restLabel(rest.length),
      value: restValue,
      children: foldSlices(rest, restLabel, max),
      folded: true,
    },
  ];
}

export interface RevenueTreeLabels {
  revenueRoot: string;
  orderCosts: string;
  margin: string;
  operatingFixedCosts: string;
  otherCategories: (count: number) => string;
  ebitda: string;
  depreciation: string;
  financialCosts: string;
  incomeTax: string;
  profit: string;
}

/** Root 'revenue' → [orderCosts, margin → [operatingFixedCosts → categories, ebitda → [depreciation, financialCosts, incomeTax, profit]]]. */
export function buildRevenueTree(
  line: IncomeStatementLine,
  labels: RevenueTreeLabels,
  categoryLabel: (code: string) => string,
): DonutNode | null {
  if (line.margin == null) return null;

  const categoryNodes: DonutNode[] = Object.entries(line.operatingFixedCosts.byCategory)
    .filter(([, value]) => value !== 0)
    .map(([code, value]) => ({ key: `category:${code}`, name: categoryLabel(code), value }));

  return {
    key: 'revenue',
    name: labels.revenueRoot,
    value: line.revenue,
    children: [
      { key: 'orderCosts', name: labels.orderCosts, value: line.orderCosts },
      {
        key: 'margin',
        name: labels.margin,
        value: line.margin,
        children: [
          {
            key: 'operatingFixedCosts',
            name: labels.operatingFixedCosts,
            value: line.operatingFixedCosts.total,
            children: foldSlices(categoryNodes, labels.otherCategories),
          },
          {
            key: 'ebitda',
            name: labels.ebitda,
            value: line.ebitda,
            children: [
              { key: 'depreciation', name: labels.depreciation, value: line.depreciation },
              { key: 'financialCosts', name: labels.financialCosts, value: line.financialCosts },
              { key: 'incomeTax', name: labels.incomeTax, value: line.incomeTax },
              { key: 'profit', name: labels.profit, value: line.profit },
            ],
          },
        ],
      },
    ],
  };
}

export interface CustomersTreeLabels {
  root: string;
  otherCustomers: (count: number) => string;
}

/** Root 'customers' (value = Σ non-null margin) → foldSlices(rows as leaf nodes). */
export function buildCustomersTree(rows: CustomerRow[], labels: CustomersTreeLabels): DonutNode | null {
  const withMargin = rows.filter((row) => row.margin != null);
  if (withMargin.length === 0) return null;
  const total = withMargin.reduce((sum, row) => sum + (row.margin as number), 0);
  const children = foldSlices(
    rows.map((row) => ({ key: row.key, name: row.name, value: row.margin, customerKey: row.key })),
    labels.otherCustomers,
  );
  return { key: 'customers', name: labels.root, value: total, children };
}

export function resolveDonutPath(
  root: DonutNode,
  path: string[],
): { chain: DonutNode[]; current: DonutNode; valid: boolean } {
  let current = root;
  const chain = [root];
  for (const key of path) {
    const next = (current.children || []).find((child) => child.key === key);
    if (!next || !next.children) return { chain: [root], current: root, valid: false };
    current = next;
    chain.push(next);
  }
  return { chain, current, valid: true };
}

// ─── Customers table ────────────────────────────────────────────────────────

export interface CustomerRow {
  key: string;
  id: string | null;
  name: string;
  taxId: string | null;
  address: string | null;
  orderCount: number;
  revenue: number;
  orderCosts: number | null;
  margin: number | null;
  marginPercent: number | null;
  share: number | null;
  orders: IncomeStatementCustomerOrder[];
}

/** Picks the `<basis>` totals per customer; drops customers with no orders counted in that basis. */
export function buildCustomerRows(
  customers: IncomeStatementCustomer[],
  basis: IncomeStatementBasis,
): CustomerRow[] {
  const rows = customers
    .map((customer) => {
      const totals = customer[basis];
      return {
        key: customer.key,
        id: customer.id,
        name: customer.name,
        taxId: customer.taxId,
        address: customer.address,
        orderCount: totals.orderCount,
        revenue: totals.revenue,
        orderCosts: totals.orderCosts,
        margin: totals.margin,
        marginPercent: totals.marginPercent,
        share: null as number | null,
        orders: customer.orders,
      };
    })
    .filter((row) => row.orderCount > 0);

  const marginSum = rows.reduce((sum, row) => (row.margin == null ? sum : sum + row.margin), 0);
  return rows.map((row) => ({
    ...row,
    share: row.margin == null || marginSum <= 0 ? null : (row.margin / marginSum) * 100,
  }));
}

export type CustomerSortKey = 'name' | 'orderCount' | 'revenue' | 'orderCosts' | 'margin' | 'marginPercent' | 'share';

export interface CustomerSort {
  key: CustomerSortKey;
  direction: 'asc' | 'desc';
}

/** Nulls always sort last regardless of direction; ties (and the name column) break by localized name. */
export function sortCustomerRows(rows: CustomerRow[], sort: CustomerSort, language: string): CustomerRow[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const byName = (a: CustomerRow, b: CustomerRow) => a.name.localeCompare(b.name, language);
  return [...rows].sort((a, b) => {
    if (sort.key === 'name') return byName(a, b) * direction;
    const left = a[sort.key];
    const right = b[sort.key];
    if (left == null && right == null) return byName(a, b);
    if (left == null) return 1;
    if (right == null) return -1;
    return (left - right) * direction || byName(a, b);
  });
}

/** Matches the company name/address (case-insensitive) or the NIP by digits only. */
export function filterCustomerRows(rows: CustomerRow[], query: string, language: string): CustomerRow[] {
  const trimmed = query.trim();
  if (!trimmed) return rows;
  const lowered = trimmed.toLocaleLowerCase(language);
  const digitsOnly = trimmed.replace(/\D/g, '');
  return rows.filter((row) => {
    if (row.name.toLocaleLowerCase(language).includes(lowered)) return true;
    if (row.address && row.address.toLocaleLowerCase(language).includes(lowered)) return true;
    if (digitsOnly && row.taxId && row.taxId.replace(/\D/g, '').includes(digitsOnly)) return true;
    return false;
  });
}

/** A 10-digit NIP formatted as NNN-NNN-NN-NN; any other value is returned unchanged. */
export function formatTaxId(taxId: string | null): string | null {
  if (!taxId) return taxId;
  const digits = taxId.replace(/\D/g, '');
  if (digits.length !== 10) return taxId;
  return digits.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1-$2-$3-$4');
}

/** Same bands as order-level `getMarginBucket`, applied to a customer's aggregated margin%. */
export function customerMarginBucket(row: CustomerRow): MarginBucket {
  if (row.orderCosts == null || row.orderCosts <= 0) return 'none';
  const marginPercent = row.marginPercent ?? 0;
  if (marginPercent < 0) return 'negative';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.low) return 'low';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.medium) return 'medium';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.good) return 'good';
  return 'excellent';
}

/** Clicking the active column toggles direction; a new column starts asc for the name, desc otherwise. */
export function nextCustomerSort(current: CustomerSort, key: CustomerSortKey): CustomerSort {
  if (current.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: key === 'name' ? 'asc' : 'desc' };
}
